/**
 * QAILogo Component (v2.0 - Platform Consistent)
 * Reusable logo for Q AI branding - matches platform green theme
 *
 * @param {string} size - 'small' | 'medium' | 'large' (32px, 48px, 80px)
 * @param {boolean} animated - Enable pulse animation
 * @param {boolean} showText - Show brand text next to logo
 * @param {string} textVariant - 'full' = "QuantX AI" | 'short' = "Q AI"
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

const SIZES = {
  small: { icon: 32, font: '1rem', radius: '8px' },
  medium: { icon: 48, font: '1.25rem', radius: '12px' },
  large: { icon: 80, font: '2rem', radius: '16px' },
  hero: { icon: 120, font: '3rem', radius: '24px' },
  xlarge: { icon: 160, font: '4rem', radius: '32px' }
};

const QAILogo = ({
  size = 'medium',
  animated = false,
  showText = false,
  textVariant = 'full'
}) => {
  const { icon, font, radius } = SIZES[size];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {/* Platform Green Q Icon */}
      <Box
        sx={{
          width: icon,
          height: icon,
          borderRadius: radius,
          background: 'linear-gradient(135deg, rgba(39, 181, 109, 0.9) 0%, rgba(34, 197, 94, 0.9) 100%)',
          border: '1px solid rgba(39, 181, 109, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 20px rgba(39, 181, 109, 0.25)',
          animation: animated ? 'qaiPulse 2s ease-in-out infinite' : 'none',
          '@keyframes qaiPulse': {
            '0%, 100%': {
              boxShadow: '0 0 20px rgba(39, 181, 109, 0.25)',
              transform: 'scale(1)'
            },
            '50%': {
              boxShadow: '0 0 40px rgba(39, 181, 109, 0.5)',
              transform: 'scale(1.02)'
            }
          }
        }}
      >
        <Typography
          sx={{
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: font,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            lineHeight: 1,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
          }}
        >
          Q
        </Typography>
      </Box>

      {/* Brand Text (optional) */}
      {showText && (
        <Typography
          sx={{
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: font,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            letterSpacing: '-0.01em'
          }}
        >
          {textVariant === 'full' ? 'QuantX AI' : 'Q AI'}
        </Typography>
      )}
    </Box>
  );
};

export default QAILogo;
